import { useLayoutEffect, useState } from "react";
import { useViewport } from "@xyflow/react";

const BASE_OFFSET = 14;

/**
 * Calculates the vertical offset for a toolbar to prevent overlap with a label.
 * Detects horizontal overlap between toolbar and label, and positions the toolbar
 * above the label when overlap is detected. Automatically updates on resize, zoom,
 * and when the active button changes (e.g., when color picker opens).
 */
export function useToolbarOffset(
  labelRef: React.RefObject<HTMLDivElement>,
  toolbarRef: React.RefObject<HTMLDivElement>,
  nodeRef: React.RefObject<HTMLDivElement>,
  activeButton: string | null,
) {
  const { zoom } = useViewport();
  const [verticalOffset, setVerticalOffset] = useState(BASE_OFFSET);

  useLayoutEffect(() => {
    const calculateOffset = () => {
      if (!labelRef.current || !toolbarRef.current || !nodeRef.current) {
        setVerticalOffset(BASE_OFFSET);
        return;
      }

      const labelRect = labelRef.current.getBoundingClientRect();
      const toolbarRect = toolbarRef.current.getBoundingClientRect();
      const nodeRect = nodeRef.current.getBoundingClientRect();

      if (
        labelRect.width === 0 ||
        toolbarRect.width === 0 ||
        nodeRect.width === 0
      ) {
        setVerticalOffset(BASE_OFFSET);
        return;
      }

      const overlapsHorizontally =
        toolbarRect.left < labelRect.right &&
        toolbarRect.right > labelRect.left;

      if (overlapsHorizontally) {
        const labelTop = labelRect.top - nodeRect.top;
        const calculatedOffset = Math.abs(labelTop) + toolbarRect.height / 2;
        setVerticalOffset(calculatedOffset);
      } else {
        setVerticalOffset(BASE_OFFSET);
      }
    };

    let rafId = requestAnimationFrame(calculateOffset);

    const resizeObserver = new ResizeObserver(() => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(calculateOffset);
    });

    if (labelRef.current) resizeObserver.observe(labelRef.current);
    if (toolbarRef.current) resizeObserver.observe(toolbarRef.current);
    if (nodeRef.current) resizeObserver.observe(nodeRef.current);

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      resizeObserver.disconnect();
    };
  }, [zoom, activeButton, labelRef, nodeRef, toolbarRef]);

  return verticalOffset;
}
